import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { Thread } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgents } from "@/features/agents/hooks/useAgents";
import { useMyInstalledAgents } from "@/features/marketplace/hooks/useMarketplace";
import { useTeamSessions } from "@/features/team-sessions/hooks/useTeamSessions";
import { ThreadListItem } from "@/features/threads/components/ThreadListItem";
import { useThreads } from "@/features/threads/hooks/useThreads";

type Group = "today" | "yesterday" | "thisWeek" | "older";

function groupOf(date: Date): Group {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const oneDay = 24 * 60 * 60 * 1000;
  if (date.getTime() >= startOfToday.getTime()) return "today";
  if (date.getTime() >= startOfToday.getTime() - oneDay) return "yesterday";
  if (date.getTime() >= startOfToday.getTime() - 7 * oneDay) return "thisWeek";
  return "older";
}

const ALL = "__all__";

export function ThreadsListPage() {
  const { t } = useTranslation("threads");
  const { t: tc } = useTranslation("common");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedAgentId, setPickedAgentId] = useState<string>("");
  const [filterAgentId, setFilterAgentId] = useState<string>(ALL);
  const [filterTeamSessionId, setFilterTeamSessionId] = useState<string>(ALL);
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useThreads({
    agent_id: filterAgentId !== ALL ? filterAgentId : undefined,
    team_session_id:
      filterTeamSessionId !== ALL ? filterTeamSessionId : undefined,
  });
  const agentsQuery = useAgents();
  const installedAgentsQuery = useMyInstalledAgents();
  const sessionsQuery = useTeamSessions();

  // Lookup id -> nome para mostrar bot/time na linha. Une agentes que o
  // usuario possui (useAgents) + agentes instalados via marketplace
  // (useMyInstalledAgents) -- threads podem referenciar qualquer um dos
  // dois conjuntos.
  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    agentsQuery.data?.forEach((a) => map.set(a.id, a.name));
    installedAgentsQuery.data?.forEach((a) => map.set(a.id, a.name));
    return map;
  }, [agentsQuery.data, installedAgentsQuery.data]);
  const sessionNameById = useMemo(() => {
    const map = new Map<string, string>();
    sessionsQuery.data?.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [sessionsQuery.data]);

  const pinnedThreads = (data ?? []).filter((th) => th.pinned_at != null);
  const grouped = useMemo(() => {
    const map: Record<Group, Thread[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };
    (data ?? [])
      .filter((th) => th.pinned_at == null)
      .forEach((th) => {
        const g = groupOf(new Date(th.last_activity_at));
        map[g].push(th);
      });
    return map;
  }, [data]);

  function handleStart() {
    if (!pickedAgentId) return;
    setPickerOpen(false);
    setPickedAgentId("");
    // Modo draft: a thread so e criada quando o usuario enviar a primeira
    // mensagem (ver ChatStream + ChatPage).
    navigate(`/threads/new?agent_id=${pickedAgentId}`);
  }

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const groups: Group[] = ["today", "yesterday", "thisWeek", "older"];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        actions={
          <Button onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("actions.new")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterAgentId} onValueChange={setFilterAgentId}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("filters.byAgent")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("filters.allAgents")}</SelectItem>
            {agentsQuery.data?.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterTeamSessionId}
          onValueChange={setFilterTeamSessionId}
        >
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder={t("filters.byTeamSession")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("filters.allTeamSessions")}</SelectItem>
            {sessionsQuery.data?.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterAgentId !== ALL || filterTeamSessionId !== ALL) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilterAgentId(ALL);
              setFilterTeamSessionId(ALL);
            }}
          >
            {tc("actions.clearFilters")}
          </Button>
        )}
      </div>

      {data && data.length === 0 ? (
        <EmptyState title={t("title")} description={t("empty")} />
      ) : (
        <div className="space-y-6">
          {pinnedThreads.length > 0 ? (
            <section>
              <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                {t("groups.pinned")}
              </h2>
              <ul className="space-y-2">
                {pinnedThreads.map((thread) => (
                  <ThreadListItem
                    key={thread.id}
                    thread={thread}
                    agentName={
                      thread.agent_id
                        ? agentNameById.get(thread.agent_id) ?? null
                        : null
                    }
                    teamSessionName={
                      thread.team_session_id
                        ? sessionNameById.get(thread.team_session_id) ?? null
                        : null
                    }
                  />
                ))}
              </ul>
            </section>
          ) : null}
          {groups.map((g) =>
            grouped[g].length === 0 ? null : (
              <section key={g}>
                <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                  {t(`groups.${g}`)}
                </h2>
                <ul className="space-y-2">
                  {grouped[g].map((thread) => (
                    <ThreadListItem
                      key={thread.id}
                      thread={thread}
                      agentName={
                        thread.agent_id
                          ? agentNameById.get(thread.agent_id) ?? null
                          : null
                      }
                      teamSessionName={
                        thread.team_session_id
                          ? sessionNameById.get(thread.team_session_id) ?? null
                          : null
                      }
                    />
                  ))}
                </ul>
              </section>
            ),
          )}
        </div>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("picker.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Nova conversa: apenas agentes INSTALADOS pelo usuario --
                eles ja passaram por publicacao + aprovacao. Agentes em
                rascunho / criados-mas-nao-instalados nao aparecem aqui
                (rascunho so e testavel pelo sandbox do editor). */}
            <Select value={pickedAgentId} onValueChange={setPickedAgentId}>
              <SelectTrigger>
                <SelectValue placeholder={t("picker.selectAgent")} />
              </SelectTrigger>
              <SelectContent>
                {installedAgentsQuery.data?.length ? (
                  installedAgentsQuery.data.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    {t("picker.noInstalled")}
                  </div>
                )}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setPickerOpen(false)}
              >
                {tc("actions.cancel")}
              </Button>
              <Button onClick={handleStart} disabled={!pickedAgentId}>
                {t("picker.start")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
